import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { getOfferInsights, OfferDraft, OfferInsights, OfferSuggestion, writeOfferWording } from '../../api';
import { categoryLabel, formatDays } from '../../features';
import { PageHead, usePortal } from './shared';

const PERIODS = [{ days: 28, label: '4 weeks' }, { days: 56, label: '8 weeks' }, { days: 91, label: '3 months' }];
const KIND_LABEL: Record<OfferSuggestion['kind'], string> = { SlowSlot: 'Quiet time', Combo: 'Popular pair', SlowItem: 'Slow dish', BillBooster: 'Bill size' };

const rupees = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;
const errorMessage = (err: unknown) => (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Could not write this offer right now.';

function describeOffer(d: OfferDraft) {
  const discount = d.discountType === 'Percentage' ? `${d.discountValue}% off` : `₹${d.discountValue} off`;
  const when = [d.activeDays.length ? formatDays(d.activeDays) : 'Every day', d.startTime && d.endTime ? `${d.startTime}–${d.endTime}` : null].filter(Boolean).join(', ');
  return [categoryLabel(d.category), discount, when, d.minimumSpend ? `min. bill ₹${d.minimumSpend}` : null].filter(Boolean).join(' · ');
}

// Offer ideas worked out from the venue's own POS bills. "Create offer" opens the normal offer editor,
// pre-filled; the partner edits, confirms accuracy and saves, and Cheers2Night reviews it as usual.
const OfferAssistantPage: React.FC = () => {
  const { to } = usePortal();
  const [days, setDays] = useState(28);
  const [data, setData] = useState<OfferInsights | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setData(null);
    setFailed(false);
    getOfferInsights(days).then(setData).catch(() => setFailed(true));
  }, [days]);

  return (
    <>
      <PageHead title="Offer Assistant" subtitle="Offer ideas from your own POS sales. You decide what to publish." />
      <div className="pp-tabs pp-tabs-inline" style={{ marginBottom: 16 }}>
        {PERIODS.map((p) => <button key={p.days} className={`pp-tab pp-sm ${days === p.days ? 'on' : ''}`} onClick={() => setDays(p.days)}>Last {p.label}</button>)}
      </div>
      {failed && <div className="pp-notice error">Could not load your sales. Please refresh.</div>}
      {!data && !failed && <p className="pp-muted">Reading your sales...</p>}
      {data && (
        <>
          <div className="pp-stats">
            <div className="pp-stat"><b>{data.orders}</b><p>Paid bills</p></div>
            <div className="pp-stat"><b>{rupees(data.netSales)}</b><p>Sales</p></div>
            <div className="pp-stat"><b>{rupees(data.averageBill)}</b><p>Average bill</p></div>
            <div className="pp-stat"><b>{data.quietestDay ?? '–'}</b><p>Quietest day</p></div>
          </div>

          {!data.enoughData ? (
            <div className="pp-panel">
              <h3 style={{ marginTop: 0 }}>Not enough sales yet</h3>
              <p className="pp-muted">
                The assistant needs at least {data.orders + data.ordersNeeded} paid bills in this period to spot patterns; {data.ordersNeeded} more to go.
                Bills are counted from C2N POS, so bill every table through it.
              </p>
              <Link className="pp-btn pp-gold" to={to('/pos')}>Open POS</Link>
            </div>
          ) : data.suggestions.length === 0 ? (
            <div className="pp-panel"><p className="pp-muted" style={{ margin: 0 }}>Sales look even across days, times and dishes, so there is nothing to suggest right now. Check again next week.</p></div>
          ) : (
            <>
              <h3 className="pp-section">Suggested offers</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                {data.suggestions.map((s) => <SuggestionCard key={s.key} suggestion={s} aiWriting={data.aiWriting} />)}
              </div>
            </>
          )}

          {data.topItems.length > 0 && (
            <>
              <h3 className="pp-section">Best sellers</h3>
              <div className="pp-panel">
                <div className="pp-table-wrap">
                  <table className="pp-table">
                    <thead><tr><th>Dish / drink</th><th className="num">Sold</th><th className="num">Sales</th></tr></thead>
                    <tbody>{data.topItems.map((t) => <tr key={t.name}><td>{t.name}</td><td className="num">{t.quantity}</td><td className="num">{rupees(t.amount)}</td></tr>)}</tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          <p className="pp-hint">Based on paid C2N POS bills from {data.from} to {data.to}. Suggestions never include alcohol offers, and every offer is reviewed by Cheers2Night before guests see it.</p>
        </>
      )}
    </>
  );
};

function SuggestionCard({ suggestion: s, aiWriting }: { suggestion: OfferSuggestion; aiWriting: boolean }) {
  const { to } = usePortal();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(s.draft);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rewrite = async () => {
    setWriting(true);
    setError(null);
    try {
      const w = await writeOfferWording(s.draft, s.reason);
      setDraft({ ...draft, title: w.title, description: w.description });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setWriting(false);
    }
  };

  return (
    <div className="pp-panel" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span className="pp-badge" style={{ alignSelf: 'flex-start' }}>{KIND_LABEL[s.kind]}</span>
      <h3 style={{ margin: 0 }}>{s.headline}</h3>
      <p className="pp-muted" style={{ margin: 0 }}>{s.reason}</p>
      <div style={{ borderTop: '1px solid rgba(255,255,255,.08)', paddingTop: 10 }}>
        <b>{draft.title}</b>
        <p style={{ margin: '4px 0' }}>{draft.description}</p>
        <small className="pp-muted">{describeOffer(draft)}</small>
      </div>
      {error && <div className="pp-notice error" style={{ margin: 0 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 'auto' }}>
        <button className="pp-btn pp-gold" onClick={() => navigate(to('/partner-account/offers/new'), { state: { draft } })}>Create this offer</button>
        {aiWriting && <button className="pp-btn pp-outline" disabled={writing} onClick={rewrite}><AutoAwesomeOutlinedIcon fontSize="small" />{writing ? 'Writing...' : 'Rewrite with AI'}</button>}
      </div>
    </div>
  );
}

export default OfferAssistantPage;
